import {connect, IConnectProps} from 'globx-react';
import pointerify, {
    Axis,
    Events,
    EventType,
    Pointerify
} from 'pointerify';
import * as React from 'react';
import {Subject}  from 'rxjs';

import StoreName              from '../../../../State/Shared/Constants/StoreName';
import Volume                 from '../../../../State/Stores/Volume/Volume';
import * as TestIds           from '../../../Shared/Constants/TestIds';
import UiEventType            from '../../../Shared/Constants/UiEventType';
import IUiEventsStreamPayload from '../../../Shared/Interfaces/IUiEventsStreamPayload';
import composeTokenList       from '../../Util/composeTokenList';
import renderTestId           from '../../Util/renderTestId';

import IconMute   from './Assets/IconMute';
import IconVolume from './Assets/IconVolume';

import * as styles from './volumeControl.css';

interface IVolumeProps extends IConnectProps<Volume> {
    areControlsIdle: boolean;
    uiEvents$: Subject<IUiEventsStreamPayload>;
}

const mapPointerEventData = (e: CustomEvent<{multiplierY: number}>): any =>
    ({
        multiplier: 1 - e.detail.multiplierY
    });

@connect(StoreName.VOLUME)
class VolumeControl extends React.Component<IVolumeProps> {
    public pointer: Pointerify;
    public trackRef: HTMLElement;

    constructor(props) {
        super(props);

        this.handleClick = this.handleClick.bind(this);
        this.handlePointerDrag = this.handlePointerDrag.bind(this);
        this.handlePointerTap = this.handlePointerTap.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
    }

    public componentDidMount(): void {
        this.pointer = pointerify(this.trackRef, {
            behavior: {
                clampAxis: Axis.Y
            },
            physics: {
                inertia: false
            }
        });

        this.trackRef.addEventListener(EventType.POINTER_DRAG, this.handlePointerDrag);
        this.trackRef.addEventListener(EventType.POINTER_TAP, this.handlePointerTap);
        this.trackRef.addEventListener(EventType.POINTER_UP, this.handlePointerUp);
    }

    public shouldComponentUpdate(nextProps: IVolumeProps): boolean {
        return (
            this.props.value.level !== nextProps.value.level ||
            this.props.value.isMutedOrForceMuted !== nextProps.value.isMutedOrForceMuted ||
            this.props.value.isSliderActive !== nextProps.value.isSliderActive ||
            this.props.areControlsIdle !== nextProps.areControlsIdle
        );
    }

    public setTrackRef = element => this.trackRef = element;

    private get levelStyle(): React.CSSProperties {
        return {
            bottom: `${this.props.value.bottom}%`
        };
    }

    public render(): JSX.Element {
        const {value, areControlsIdle} = this.props;
        const {isMutedOrForceMuted} = value;

        const muteUnmuteTestIdBase = isMutedOrForceMuted ?
            TestIds.PLAYER_BUTTON_UNMUTE :
            TestIds.PLAYER_BUTTON_MUTE;

        const className = composeTokenList([
            styles.volume,
            [value.isSliderActive, styles.volumeActive]
        ]);

        const volumeControlsTestId = composeTokenList([
            TestIds.PLAYER_VOLUME_CONTROL,
            [!areControlsIdle, TestIds.PLAYER_VOLUME_CONTROL_VISIBLE]
        ]);

        const muteUnmuteTestId = composeTokenList([
            muteUnmuteTestIdBase,
            [!areControlsIdle, muteUnmuteTestIdBase + '_VISIBLE']
        ]);

        return (
            <div
                className={className}
                data-test-id={renderTestId(volumeControlsTestId)}
            >
                <div className={styles.volumeSlider}>
                    <div className={styles.volumeLabel}>{this.props.value.humanReadableVolumeLevel}</div>
                    <div
                        className={styles.volumeTrack}
                        ref={this.setTrackRef}
                        data-test-id={renderTestId(TestIds.PLAYER_VOLUME_TRACK)}
                    >
                        <div style={this.levelStyle} className={styles.volumeLevel}/>
                    </div>
                </div>

                <button
                    className={styles.volumeMuteUnmute}
                    onClick={this.handleClick}
                    data-test-id={renderTestId(muteUnmuteTestId)}
                >
                    {isMutedOrForceMuted ? <IconMute/> : <IconVolume/>}
                </button>
            </div>
        );
    }

    public componentWillUnmount(): void {
        this.trackRef.removeEventListener(EventType.POINTER_DRAG, this.handlePointerDrag);
        this.trackRef.removeEventListener(EventType.POINTER_TAP, this.handlePointerTap);
        this.trackRef.removeEventListener(EventType.POINTER_UP, this.handlePointerUp);

        this.pointer.destroy();
    }

    private handleClick(): void {
        this.props.uiEvents$.next({
            type: UiEventType.VOLUME_TAP
        });
    }

    private handlePointerTap(e: Events.PointerTapEvent): void {
        this.props.uiEvents$.next({
            type: UiEventType.VOLUME_SLIDER_TAP,
            eventData: mapPointerEventData(e)
        });
    }

    private handlePointerDrag(e: Events.PointerDragEvent): void {
        this.props.uiEvents$.next({
            type: UiEventType.VOLUME_SLIDER_DRAG,
            eventData: mapPointerEventData(e)
        });
    }

    private handlePointerUp(e: Events.PointerUpEvent): void {
        this.props.uiEvents$.next({
            type: UiEventType.VOLUME_SLIDER_DRAG_END,
            eventData: mapPointerEventData(e)
        });
    }
}

export default VolumeControl;